import { partnerService, Partner } from "./partnerService";
import { UserData } from "../model/User";
import axios from "axios";

export interface WellSuperApp extends Partner {
  guardianId: number | undefined;
  hospitalName?: string; // Store mapped hospital name (e.g., "STH") for agreement lookup
}

// WellSuperApp service.
// This service is used to store and retrieve the WellSuperApp object.
//
const partnerWellSuperAppService = {
  isActive: () => {
    return !!getPartnerObject();
  },

  onIndexMounted: async (location: Location) => {
    const partner = getPartnerObject();
    if (!partner) return;

    // Hospital ID should be passed from the well landing page.
    const hospitalId = new URLSearchParams(location.search).get("hospitalId");
    const numHospitalId = hospitalId !== null ? parseInt(hospitalId, 10) : NaN;
    if (!isNaN(numHospitalId)) {
      partner.hospitalId = numHospitalId;

      // Fetch hospital list and map hospitalId to HospitalName
      try {
        const hospitalList = await fetchHospitalList();
        const matchedHospital = hospitalList.find(
          (h: any) => h.HospitalId === numHospitalId
        );
        if (matchedHospital) {
          // Store hospital name (e.g., "STH") to use as PartnerName for GetAgreement
          partner.hospitalName = matchedHospital.HospitalName;
        } else {
          // No match found — clear any stale hospitalName from a previous run so
          // agreement lookup doesn't silently use the wrong PartnerName.
          partner.hospitalName = undefined;
        }
      } catch (error) {
        console.error("Failed to fetch hospital list:", error);
      }

      setPartnerObject(partner);
    }

    // Parse guardian ID from query string.
    const guardianId = new URLSearchParams(location.search).get("gId") as
      | string
      | undefined;
    if (guardianId) {
      partner.guardianId = parseInt(guardianId);
      setPartnerObject(partner);
    }
  },

  onIndexMountedRequiredRegister: (userData: UserData | undefined) => {
    return !userData?.firstName || !userData?.lastName || !userData?.tel;
  },

  onRegisterUpdateUserDataToWell: async (userData: UserData) => {
    let gender = userData.gender as string | null;
    if (gender === "ไม่ระบุ" || gender === "0" || !gender) {
      gender = null;
    } else {
      gender = gender.toLowerCase() === "male" || gender === "ชาย" ? "M" : "F";
    }

    const idToken = userData.idToken;

    const partner = getPartnerObject();
    if (partner && partner.guardianId) {
      const response = await axios.post(
        process.env.REACT_APP_WELLSUPERAPP_GUARDIAN_UPDATE_URL as string,
        {
          updateGuardianReqBean: {
            guardianId: partner.guardianId,
            email: userData.email,
            passportId: userData.idCard,
            firstName: userData.firstName,
            lastName: userData.lastName,
            phoneNumber: userData.tel,
            birthDate: userData.birthdate,
            gender: gender,
          },
        },
        {
          headers: {
            Authorization: "Bearer " + idToken,
          },
        }
      );

      // ISO27018: do not log response object (may contain PII or Authorization headers)
      // console.log({ WellUpdateGuardianResponse: response });
    } else {
      const response = await axios.post(
        process.env.REACT_APP_WELLSUPERAPP_CUSTOMER_UPDATE_URL as string,
        {
          updateCustomerReqBean: {
            email: userData.email,
            passportId: userData.idCard,
            firstName: userData.firstName,
            lastName: userData.lastName,
            phoneNumber: userData.tel,
            birthDate: userData.birthdate,
            gender: gender,
          },
        },
        {
          headers: {
            Authorization: "Bearer " + idToken,
          },
        }
      );

      // ISO27018: do not log response object (may contain PII or Authorization headers)
      // console.log({ WellUpdateCustomerResponse: response });
    }
  },
};

// Module-level cache — reuse the same promise across all callers (Agreement init, agree flow, etc.)
let hospitalListPromise: Promise<any[]> | null = null;

// Fetch hospital list from API (cached)
const fetchHospitalList = (): Promise<any[]> => {
  if (!hospitalListPromise) {
    hospitalListPromise = axios
      .get(process.env.REACT_APP_BASE_URL_V2 + "/Hospital/Hospitals")
      .then((r) => r.data)
      .catch((err) => {
        // Clear cache on failure so the next call retries instead of
        // permanently returning the rejection.
        hospitalListPromise = null;
        throw err;
      });
  }
  return hospitalListPromise;
};

const getPartnerObject = () => {
  const partner = partnerService.getPartner();
  if (
    partner &&
    (partner.name.trim() === "WellSuperApp" ||
      partner.name.trim() === "Well_AIDOCTOR")
  ) {
    return partner as WellSuperApp;
  }
  return undefined;
};

const setPartnerObject = (partner: WellSuperApp) => {
  partnerService.setPartner(partner);
};

export { partnerWellSuperAppService };
